import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { BrantaService, type Payment } from '@branta-ops/branta/v2'
import Button from '../../../components/Button'
import ErrorMessage from '../../../components/Error'
import ButtonsOnBottom from '../../../components/ButtonsOnBottom'
import { NavigationContext, Pages } from '../../../providers/navigation'
import { FlowContext } from '../../../providers/flow'
import Padded from '../../../components/Padded'
import { isBTCAddress, decodeArkAddress, isLightningInvoice, isURLWithLightningQueryString } from '../../../lib/address'
import { AspContext } from '../../../providers/asp'
import { isArkNote } from '../../../lib/arknote'
import InputAmount, { type InputAmountMode } from '../../../components/InputAmount'
import InputAddress from '../../../components/InputAddress'
import Header from '../../../components/Header'
import { WalletContext } from '../../../providers/wallet'
import { fromSatoshis, prettyAmount, prettyFiatAmount, prettyNumber, toSatoshis } from '../../../lib/format'
import Content from '../../../components/Content'
import FlexCol from '../../../components/FlexCol'
import FlexRow from '../../../components/FlexRow'
import Keyboard, { KeyboardInputMode } from '../../../components/Keyboard'
import Text from '../../../components/Text'
import Shadow from '../../../components/Shadow'
import Scanner from '../../../components/Scanner'
import LoadingLogo from '../../../components/LoadingLogo'
import { consoleError } from '../../../lib/logs'
import { Addresses, AssetOption, Currencies, Themes, Unit } from '../../../lib/types'
import { aspErrorText, getReceivingAddresses } from '../../../lib/asp'
import { isMobileBrowser } from '../../../lib/browser'
import { ConfigContext } from '../../../providers/config'
import { FiatContext } from '../../../providers/fiat'
import { ArkNote, AssetDetails, isValidArkAddress, type NetworkName } from '@arkade-os/sdk'
import { LimitsContext } from '../../../providers/limits'
import { checkLnUrlConditions, fetchInvoice, fetchArkAddress, isValidLnUrl, LnUrlResponse } from '../../../lib/lnurl'
import { extractError } from '../../../lib/error'
import { decodeInvoice } from '../../../lib/bolt11'
import { lnSendRendezvous, requestLnSend } from '../../../lib/lnSwap'
import { withRfqTransport } from '../../../lib/nostrRfq'
import { discoverMarkets } from '../../../lib/swapMarkets'
import { decodeBip21, isBip21 } from '../../../lib/bip21'
import { InfoLine } from '../../../components/Info'
import { centsToUnits, prettyAssetAmount, unitsToCents } from '../../../lib/assets'
import { FeesContext } from '../../../providers/fees'
import SheetModal from '../../../components/SheetModal'
import { AnimatePresence, motion } from 'framer-motion'
import { overlaySlideUp, overlayStyle } from '../../../lib/animations'
import { useReducedMotion } from '../../../hooks/useReducedMotion'
import TokenLogo, { tokenLogoTickerForTicker } from '../../../components/TokenLogo'
import {
  designatedAccountCurrency,
  normalizeAssetMinorUnits,
  rawAssetPresentation,
  verifiedDesignatedCurrency,
} from '../../../lib/accountAssets'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu'
import { hapticLight } from '../../../lib/haptics'
import { getEmulatorPubkeyForNetwork, testDomains } from '../../../lib/constants'
import UnverifiedBadge from '../../../components/UnverifiedBadge'

const isProductionEnv = !testDomains.some((d) => window.location.hostname.includes(d))

const brantaClient = new BrantaService({
  baseUrl: isProductionEnv ? 'Production' : 'Staging',
  privacy: 'strict',
})

export const isPlainOnchainTypedRecipient = (value: string): boolean => {
  if (isBTCAddress(value)) return true
  if (!isBip21(value.toLowerCase())) return false

  try {
    const decoded = decodeBip21(value)
    return Boolean(
      decoded.address &&
        isBTCAddress(decoded.address) &&
        !decoded.arkAddress &&
        !decoded.invoice &&
        !decoded.lnUrl &&
        !decoded.assetId,
    )
  } catch {
    return false
  }
}

function AssetIcon({ asset }: { asset: AssetOption | null }) {
  const { aspInfo } = useContext(AspContext)
  const { isVerifiedAsset } = useContext(WalletContext)
  // official token logos are pinned to verified asset IDs, not self-reported
  // tickers; designated assets wear their currency account's flag
  const verified = Boolean(asset) && isVerifiedAsset(asset!.assetId)
  const currency = verified ? designatedAccountCurrency(aspInfo.network, asset!.assetId) : undefined
  const tokenTicker = !asset
    ? 'BTC'
    : currency
      ? tokenLogoTickerForTicker(currency)
      : verified
        ? tokenLogoTickerForTicker(asset.ticker)
        : null

  if (tokenTicker) {
    return (
      <span className='send-asset-icon' aria-hidden='true'>
        <TokenLogo ticker={tokenTicker} />
      </span>
    )
  }

  if (asset?.icon) {
    return <img className='send-asset-icon' src={asset.icon} alt='' />
  }

  return (
    <span className='send-asset-icon send-asset-icon--fallback' aria-hidden='true'>
      {asset?.ticker?.[0] ?? 'A'}
    </span>
  )
}

export default function SendForm() {
  const { aspInfo } = useContext(AspContext)
  const { config, effectiveTheme, useFiat } = useContext(ConfigContext)
  const { calcOnchainOutputFee } = useContext(FeesContext)
  const { toFiat, fromFiat, fiatDecimals } = useContext(FiatContext)
  const { sendInfo, setNoteInfo, setSendInfo } = useContext(FlowContext)
  const { amountIsAboveMaxLimit, amountIsBelowMinLimit, utxoTxsAllowed, vtxoTxsAllowed } = useContext(LimitsContext)
  const { navigate } = useContext(NavigationContext)
  const {
    assetBalances,
    availableAssetBalances,
    assetMetadataCache,
    availableBalance,
    balance,
    isVerifiedAsset,
    setCacheEntry,
    svcWallet,
  } = useContext(WalletContext)

  const [amount, setAmount] = useState<number>()
  const [amountTextValue, setAmountTextValue] = useState('')
  const [amountIsReadOnly, setAmountIsReadOnly] = useState(false)
  const [assetOptions, setAssetOptions] = useState<AssetOption[]>([])
  const [deductFromAmount, setDeductFromAmount] = useState(false)
  const [error, setError] = useState('')
  const [focus, setFocus] = useState('recipient')
  const [label, setLabel] = useState('')
  const [lnUrlResponse, setLnUrlResponse] = useState<LnUrlResponse>()
  const [keys, setKeys] = useState(false)
  const [proceed, setProceed] = useState(false)
  const [processing, setProcessing] = useState(false)
  const [readyToParse, setReadyToParse] = useState(false)
  const [recipient, setRecipient] = useState('')
  const [recipientError, setRecipientError] = useState('')
  const [receivingAddresses, setReceivingAddresses] = useState<Addresses>()
  const [scan, setScan] = useState(false)
  const [rawScanData, setRawScanData] = useState('')
  const [brantaPayment, setBrantaPayment] = useState<Payment | null>(null)
  const [brantaVerifyUrl, setBrantaVerifyUrl] = useState<string | undefined>(undefined)
  const [brantaLoading, setBrantaLoading] = useState(false)
  const [selectedAsset, setSelectedAsset] = useState<AssetOption | null>(null)
  const [showAssetSelector, setShowAssetSelector] = useState(false)
  const [showReserveModal, setShowReserveModal] = useState(false)
  const [valueSats, setValueSats] = useState<number | undefined>(undefined)

  const timeoutRef = useRef<NodeJS.Timeout>()

  const prefersReducedMotion = useReducedMotion()
  const accountAsset = useMemo<AssetOption | null>(
    () =>
      sendInfo.account
        ? {
            assetId: sendInfo.account.assetId,
            balance: sendInfo.account.balance,
            decimals: sendInfo.account.decimals,
            name: sendInfo.account.ticker,
            ticker: sendInfo.account.ticker,
            // currency accounts only exist for id-verified designated assets
            trusted: true,
          }
        : null,
    [sendInfo.account],
  )
  const activeAsset = accountAsset ?? selectedAsset
  const isAssetSend = activeAsset !== null

  const DUST_AMOUNT = 330
  const RECIPIENT_DEBOUNCE_MS = 800
  const hasAssets = assetBalances.length > 0
  const reserveApplied = !isAssetSend && hasAssets
  // clamp: a balance below the reserve is "nothing sendable", not a negative
  // amount (and the provider's availableBalance never flashes 0 on mount)
  const liquidBalance = Math.max(0, availableBalance - (reserveApplied ? DUST_AMOUNT : 0))

  const smartSetError = (str: string) => {
    setError(str === '' ? (aspInfo.unreachable ? aspErrorText(aspInfo, 'Arkade server unreachable') : '') : str)
  }

  // Prefer display-currency entry when conversion is available; otherwise
  // fall back to the wallet's bitcoin unit without reinterpreting the text.
  const currencyConversionUseful = config.currency !== Currencies.BTC && toFiat(100_000_000) > 0 && fromFiat(1) > 0
  const [entryMode, setEntryMode] = useState<InputAmountMode>(useFiat && currencyConversionUseful ? 'fiat' : 'unit')
  const fiatEntry = entryMode === 'fiat' && useFiat && currencyConversionUseful

  useEffect(() => {
    if (currencyConversionUseful || entryMode !== 'fiat') return
    setEntryMode('unit')
    setAmountTextValue(sendInfo.satoshis ? getTextValue(sendInfo.satoshis, false) : '')
  }, [config.unit, currencyConversionUseful, entryMode, sendInfo.satoshis])

  const getTextValue = (sats: number, fiat = fiatEntry) =>
    fiat
      ? prettyNumber(toFiat(sats), fiatDecimals(), false)
      : config.unit === Unit.BTC
        ? prettyNumber(fromSatoshis(sats), 8, false)
        : prettyNumber(sats, 0, false)

  const handleEntryModeChange = (mode: InputAmountMode) => {
    setEntryMode(mode)
    // re-express the field text from the authoritative sats, which the toggle
    // never changes — parsing re-expressed text with the previous mode's
    // closure once stored a fiat string as raw sats (a wrong-amount send)
    const sats = sendInfo.satoshis
    if (!sats) return
    setAmountTextValue(getTextValue(sats, mode === 'fiat' && useFiat && currencyConversionUseful))
    setValueSats(sats)
  }

  const prettyUnitBalance = (sats: number) =>
    config.unit === Unit.BTC ? prettyAmount(fromSatoshis(sats), config.unit, 8) : prettyAmount(sats)

  useEffect(() => {
    if (!sendInfo.scan) return
    const nextSendInfo = { ...sendInfo }
    delete nextSendInfo.scan
    setKeys(false)
    setScan(true)
    setSendInfo(nextSendInfo)
  }, [sendInfo.scan])

  // cleanup debounce timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
  }, [])

  // get receiving addresses
  useEffect(() => {
    if (!svcWallet) return
    getReceivingAddresses(svcWallet)
      .then(({ boardingAddr, offchainAddr }) => {
        if (!boardingAddr || !offchainAddr) {
          throw new Error('unable to get receiving addresses')
        }
        setReceivingAddresses({ boardingAddr, offchainAddr })
      })
      .catch(smartSetError)
  }, [])

  // build asset options from balances + metadata
  useEffect(() => {
    if (!config.apps.assets.enabled) return
    const loadOptions = async () => {
      if (!svcWallet) return
      const options: AssetOption[] = []
      for (const ab of assetBalances) {
        let meta: AssetDetails | undefined = assetMetadataCache.get(ab.assetId)
        if (!meta) {
          try {
            const fetched = await svcWallet.assetManager.getAssetDetails(ab.assetId)
            if (fetched) meta = setCacheEntry(ab.assetId, fetched)
          } catch (err) {
            consoleError(err, `error fetching metadata for ${ab.assetId}`)
          }
        }
        const presentation = rawAssetPresentation(meta?.metadata, `${ab.assetId.slice(0, 8)}...`)
        options.push({
          assetId: ab.assetId,
          // list membership follows owned assets so one fully in escrow still
          // appears, but the amount offered is only ever the spendable part
          balance: availableAssetBalances.find((a) => a.assetId === ab.assetId)?.amount ?? BigInt(0),
          name: presentation.name,
          ticker: presentation.ticker,
          icon: presentation.icon,
          decimals: meta?.metadata?.decimals ?? 8,
          trusted: isVerifiedAsset(ab.assetId),
        })
      }
      setAssetOptions(options)
    }
    loadOptions()
  }, [svcWallet, assetBalances, availableAssetBalances, config.apps.assets.enabled])

  // initialize selected asset from pre-set sendInfo.assets (e.g. from Asset Detail page)
  useEffect(() => {
    if (sendInfo.account) {
      setSelectedAsset(null)
      return
    }
    if (!sendInfo.assets?.length || assetOptions.length === 0) return
    const presetAssetId = sendInfo.assets[0].assetId
    const found = assetOptions.find((a) => a.assetId === presetAssetId)
    if (found && !selectedAsset) setSelectedAsset(found)
  }, [assetOptions, sendInfo.account, sendInfo.assets])

  // parse recipient data
  // repeat when asset changes to re-validate addresses (e.g. if user
  // selects an asset and the address is not compatible with it)
  useEffect(() => {
    if (!readyToParse) return
    setRecipientError('')
    const parseRecipient = async () => {
      if (!recipient) return
      const lowerCaseData = recipient.toLowerCase().replace(/^lightning:/, '')
      if (isURLWithLightningQueryString(recipient)) {
        const url = new URL(recipient)
        return setRecipient(url.searchParams.get('lightning')!)
      }
      if (isBip21(lowerCaseData)) {
        const { address, arkAddress, invoice, lnUrl, satoshis, assetId, assetAmount } = decodeBip21(recipient.trim())
        if (!address && !arkAddress && !invoice && !lnUrl) return setRecipientError('Unable to parse bip21')
        if (assetId) {
          let found = assetOptions.find((a) => a.assetId === assetId)
          if (!found) {
            let meta: AssetDetails | undefined = assetMetadataCache.get(assetId)
            if (!meta && svcWallet) {
              try {
                const fetched = await svcWallet.assetManager.getAssetDetails(assetId)
                if (fetched) meta = setCacheEntry(assetId, fetched)
              } catch (err) {
                consoleError(err, `error fetching metadata for ${assetId}`)
              }
            }
            const presentation = rawAssetPresentation(meta?.metadata, `${assetId.slice(0, 8)}...`)
            found = {
              assetId,
              balance: BigInt(0),
              name: presentation.name,
              ticker: presentation.ticker,
              icon: presentation.icon,
              decimals: meta?.metadata?.decimals ?? 8,
              trusted: isVerifiedAsset(assetId),
            }
          }
          setSelectedAsset(found)
          const rawAmount = assetAmount ? unitsToCents(assetAmount, found.decimals) : BigInt(0)
          return setSendInfo((prev) => ({
            ...prev,
            address,
            arkAddress,
            invoice,
            recipient,
            satoshis: 0,
            assets: [{ assetId, amount: rawAmount }],
            pendingLnSend: invoice === prev.invoice ? prev.pendingLnSend : undefined,
          }))
        }
        setSendInfo((prev) => ({
          ...prev,
          account: prev.account,
          address,
          arkAddress,
          assets: prev.assets,
          invoice,
          lnUrl,
          recipient,
          satoshis: satoshis ?? prev.satoshis,
          pendingLnSend: invoice === prev.invoice ? prev.pendingLnSend : undefined,
        }))
        if (satoshis) setAmountTextValue(getTextValue(satoshis))
        return
      }
      if (isValidArkAddress(lowerCaseData)) {
        return setSendInfo((prev) => ({ ...prev, arkAddress: lowerCaseData, pendingLnSend: undefined }))
      }
      if (isLightningInvoice(lowerCaseData)) {
        if (isAssetSend) {
          return setRecipientError('Assets can only be sent to Arkade addresses')
        }
        // Amount from the wallet's own decoder; expiry and chain are re-checked
        // by the RFQ client before any solver sees the invoice.
        let satoshis = 0
        try {
          satoshis = decodeInvoice(lowerCaseData).amountSats
        } catch {
          return setRecipientError('Unable to decode invoice')
        }
        if (!satoshis) return setRecipientError('Invoice must have amount defined')
        setSendInfo((prev) => ({
          ...prev,
          invoice: lowerCaseData,
          satoshis,
          pendingLnSend: lowerCaseData === prev.invoice ? prev.pendingLnSend : undefined,
        }))
        setAmountTextValue(getTextValue(satoshis))
        setAmountIsReadOnly(true)
        return
      }
      if (isBTCAddress(recipient)) {
        if (isAssetSend) {
          return setRecipientError('Assets can only be sent to Arkade addresses')
        }
        return setSendInfo({ ...sendInfo, address: recipient })
      }
      if (isArkNote(lowerCaseData)) {
        try {
          const { value } = ArkNote.fromString(recipient)
          setNoteInfo({ note: recipient, satoshis: value })
          return navigate(Pages.NotesRedeem)
        } catch (err) {
          consoleError(err, 'error parsing ark note')
        }
      }
      if (isValidLnUrl(lowerCaseData)) {
        return setSendInfo({ ...sendInfo, lnUrl: lowerCaseData })
      }
      setRecipientError('Invalid recipient address')
      setReadyToParse(false)
    }
    parseRecipient()
  }, [recipient, isAssetSend, readyToParse])

  // fetch branta payment info for the current recipient (SDK strict mode gates non-ZK)
  useEffect(() => {
    const typed = recipient.trim()
    if (!rawScanData && !typed) {
      setBrantaPayment(null)
      setBrantaVerifyUrl(undefined)
      setBrantaLoading(false)
      return
    }

    setBrantaPayment(null)
    setBrantaVerifyUrl(undefined)

    if (!rawScanData && isPlainOnchainTypedRecipient(typed)) {
      setBrantaLoading(false)
      return
    }

    let cancelled = false

    const runLookup = () => {
      if (cancelled) return
      setBrantaLoading(true)
      const lookup = rawScanData ? brantaClient.getPaymentsByQrCode(rawScanData) : brantaClient.getPayments(typed)

      lookup
        .then(({ payments, verifyUrl }) => {
          if (cancelled) return
          const payment = payments?.[0] ?? null
          if (!payment) {
            setBrantaPayment(null)
            setBrantaVerifyUrl(undefined)
            return
          }
          const isHttpsUrl = (val: unknown): boolean => typeof val === 'string' && val.startsWith('https://')
          setBrantaPayment({
            ...payment,
            platformLogoUrl: isHttpsUrl(payment.platformLogoUrl) ? payment.platformLogoUrl : undefined,
            platformLogoLightUrl: isHttpsUrl(payment.platformLogoLightUrl) ? payment.platformLogoLightUrl : undefined,
          })
          setBrantaVerifyUrl(isHttpsUrl(verifyUrl) ? verifyUrl : undefined)
        })
        .catch((err) => {
          if (cancelled) return
          consoleError('Branta API error', err)
          setBrantaPayment(null)
          setBrantaVerifyUrl(undefined)
        })
        .finally(() => {
          if (cancelled) return
          setBrantaLoading(false)
        })
    }

    // QR scans verify immediately; typed input is debounced to avoid one request per keystroke
    const timer = rawScanData ? null : setTimeout(runLookup, 400)
    if (rawScanData) runLookup()

    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [rawScanData, recipient])

  // check lnurl limits
  useEffect(() => {
    if (!lnUrlResponse) return
    const { satoshis } = sendInfo
    const { minSendable: min, maxSendable: max } = lnUrlResponse
    if (!min || !max) return
    if (min > balance) return setError('Insufficient funds for LNURL')
    if (satoshis && satoshis < min) return setError(`Amount below LNURL min limit`)
    if (satoshis && satoshis > max) return setError(`Amount above LNURL max limit`)
    if (min === max) {
      setAmountIsReadOnly(true)
    } else {
      setAmountIsReadOnly(false)
    }
  }, [lnUrlResponse])

  // check lnurl conditions
  useEffect(() => {
    if (!sendInfo.lnUrl) return
    if (sendInfo.arkAddress) return
    if (sendInfo.lnUrl && sendInfo.invoice) return
    checkLnUrlConditions(sendInfo.lnUrl)
      .then((conditions) => {
        if (!conditions) return setRecipientError('Unable to fetch LNURL conditions')
        const min = Math.floor(conditions.minSendable / 1000) // from millisatoshis to satoshis
        const max = Math.floor(conditions.maxSendable / 1000) // from millisatoshis to satoshis
        // when the LNURL resolves to a fixed amount, set amountTextValue
        if (min === max) {
          setSendInfo({ ...sendInfo, satoshis: min })
          setAmountTextValue(getTextValue(min))
          setAmountIsReadOnly(true)
        }
        return setLnUrlResponse({ ...conditions, minSendable: min, maxSendable: max })
      })
      .catch((e) => {
        if (e.status === 404) {
          consoleError(e, 'LNURL not found')
          setRecipientError('LNURL not found')
          return
        }
        consoleError(e, 'Error checking LNURL conditions')
        setRecipientError(extractError(e))
      })
  }, [sendInfo.arkAddress, sendInfo.lnUrl])

  // check if user wants to send all funds
  useEffect(() => {
    if (sendInfo.lnUrl && sendInfo.satoshis === balance) handleSendAll()
  }, [sendInfo.lnUrl])

  // validate recipient addresses
  useEffect(() => {
    if (!receivingAddresses) return
    const { offchainAddr } = receivingAddresses
    const { address, arkAddress, invoice, lnUrl } = sendInfo
    // check server limits for onchain transactions
    if (address && !arkAddress && !invoice && !lnUrl && !utxoTxsAllowed()) {
      return setRecipientError('Sending onchain not allowed')
    }
    // check server limits for offchain transactions
    if (!address && (arkAddress || invoice || lnUrl) && !vtxoTxsAllowed()) {
      return setRecipientError('Sending offchain not allowed')
    }
    // check if server key is valid
    if (arkAddress && arkAddress.length > 0) {
      const { serverPubKey } = decodeArkAddress(arkAddress)
      const { serverPubKey: expectedServerPubKey } = decodeArkAddress(offchainAddr)
      if (serverPubKey !== expectedServerPubKey) {
        // if there's no other way to pay, show error
        if (!address && !invoice) return setRecipientError('Arkade server key mismatch')
        // remove ark address from possibilities to send and continue
        // we will try to pay to lightning or mainnet instead
        setSendInfo({ ...sendInfo, arkAddress: '' })
      }
    }
    // everything is ok, clean error
    setRecipientError('')
  }, [receivingAddresses, sendInfo.address, sendInfo.arkAddress, sendInfo.invoice, sendInfo.lnUrl])

  // manage button label and errors
  useEffect(() => {
    if (isAssetSend && activeAsset) {
      const assetAmount = sendInfo.account?.amount ?? sendInfo.assets?.[0]?.amount ?? BigInt(0)
      setLabel(assetAmount > activeAsset.balance ? 'Insufficient asset balance' : 'Continue')
      return
    }
    const satoshis = sendInfo.satoshis ?? 0
    setLabel(
      satoshis > liquidBalance
        ? 'Insufficient funds'
        : lnUrlResponse?.minSendable && satoshis < lnUrlResponse.minSendable
          ? 'Amount below LNURL min limit'
          : lnUrlResponse?.maxSendable && satoshis > lnUrlResponse.maxSendable
            ? 'Amount above LNURL max limit'
            : satoshis && satoshis < 1
              ? 'Amount below 1 satoshi'
              : amountIsAboveMaxLimit(satoshis)
                ? 'Amount above max limit'
                : satoshis && amountIsBelowMinLimit(satoshis)
                  ? 'Amount below min limit'
                  : 'Continue',
    )
  }, [sendInfo.satoshis, sendInfo.assets, sendInfo.account, liquidBalance, activeAsset])

  // manage server unreachable error
  useEffect(() => {
    const errTxt = aspErrorText(aspInfo, 'Arkade server unreachable')
    if (!aspInfo.unreachable) {
      // Server reachable again: clear either unavailable variant we may have
      // shown (generic unreachable or the outdated-client message) without
      // clobbering unrelated errors.
      const outdatedTxt = aspErrorText({ ...aspInfo, outdated: true }, errTxt)
      setError((prev) => (prev === errTxt || prev === outdatedTxt ? '' : prev))
      return
    }
    setError(errTxt)
    setLabel('Server unreachable')
  }, [aspInfo.unreachable, aspInfo.outdated])

  // proceed to next step
  useEffect(() => {
    if (!proceed) return
    if (!sendInfo.address && !sendInfo.arkAddress && !sendInfo.invoice) return
    // Everything except an un-negotiated invoice goes straight through: an ark
    // address, an on-chain address, and an invoice whose quote is already in
    // hand all have all they need to be signed on the next screen.
    if (!sendInfo.invoice || sendInfo.pendingLnSend || sendInfo.arkAddress) return navigate(Pages.SendDetails)
    {
      // RFQ Lightning send: negotiate a quote over Nostr, derive the covenant
      // locally, verify, and carry the address+amount to the pay screen. The
      // negotiation is the only interactive step — funding IS acceptance.
      const negotiate = async () => {
        if (!svcWallet) return handleError('Wallet not ready')
        const network = aspInfo.network as NetworkName
        // No emulator URL is looked up here: this corridor needs the co-signer's
        // x-only KEY, never an endpoint. It rides the solver's own card; the
        // per-network pin is passed as the fallback for cards that predate the
        // field (see lnSendRendezvous). Neither available yields no rendezvous,
        // which the line below already reports.
        const rendezvous = lnSendRendezvous(await discoverMarkets(network), getEmulatorPubkeyForNetwork(network))
        if (!rendezvous) return handleError('No Lightning solver available')
        const sats = sendInfo.satoshis ?? 0
        if (sats < rendezvous.minSats || sats > rendezvous.maxSats) {
          return handleError(
            `Amount outside solver bounds (${prettyNumber(rendezvous.minSats)}-${prettyNumber(rendezvous.maxSats)} sats)`,
          )
        }
        await withRfqTransport(rendezvous, async (transport) => {
          const pendingLnSend = await requestLnSend({
            wallet: svcWallet,
            arkServerUrl: aspInfo.url,
            transport,
            invoice: sendInfo.invoice!,
            network,
            rendezvous,
          })
          setSendInfo((prev) => ({ ...prev, pendingLnSend }))
        })
      }
      negotiate().catch(handleError)
    }
  }, [proceed, sendInfo.address, sendInfo.arkAddress, sendInfo.invoice, sendInfo.pendingLnSend])

  // deal with fees deduction from amount
  useEffect(() => {
    const satoshis = sendInfo.satoshis ?? 0
    const onlyBtcAddress = sendInfo.address && !sendInfo.arkAddress && !sendInfo.invoice
    if (sendInfo.arkAddress) {
      setDeductFromAmount(false)
    } else if (onlyBtcAddress) {
      const fees = calcOnchainOutputFee()
      setDeductFromAmount(satoshis + fees > liquidBalance)
    } else {
      setDeductFromAmount(false)
    }
  }, [liquidBalance, sendInfo.satoshis, sendInfo.address, sendInfo.arkAddress, sendInfo.invoice, sendInfo.lnUrl])

  if (!svcWallet) return <LoadingLogo text='Loading...' />

  const handleError = (err: any) => {
    consoleError(err, 'error sending payment')
    setError(extractError(err))
    setProcessing(false)
  }

  const handleAmountChange = (value: string) => {
    setValueSats(undefined)
    setAmountTextValue(value)
    if (isAssetSend) {
      if (sendInfo.account) {
        const accountAmount = unitsToCents(value, sendInfo.account.decimals)
        setSendInfo({
          ...sendInfo,
          account: { ...sendInfo.account, amount: accountAmount },
          assets: [
            {
              assetId: sendInfo.account.source.assetId,
              amount: normalizeAssetMinorUnits(
                accountAmount,
                sendInfo.account.decimals,
                sendInfo.account.source.decimals,
              ),
            },
          ],
          satoshis: 0,
        })
      } else if (selectedAsset) {
        const decimals = selectedAsset?.decimals
        const cents = unitsToCents(value, decimals)
        setSendInfo({
          ...sendInfo,
          assets: [{ assetId: selectedAsset.assetId, amount: cents }],
          satoshis: 0,
        })
      }
    } else {
      const num = Number(value)
      if (Number.isNaN(num) || !Number.isFinite(num)) return setError('Invalid amount')
      const sats = fiatEntry ? fromFiat(num) : config.unit === Unit.BTC ? toSatoshis(num) : Math.floor(num)
      setSendInfo({ ...sendInfo, satoshis: sats })
    }
  }

  const handleKeyboardAmountSave = (value: string, inputMode: KeyboardInputMode) => {
    setKeys(false)
    if (inputMode === 'asset') return handleAmountChange(value)
    // the form field adopts whichever denomination the keyboard was last in,
    // so the saved text needs no re-expression — just its sats equivalent
    const sats =
      inputMode === 'fiat' ? fromFiat(Number(value)) : inputMode === 'btc' ? toSatoshis(Number(value)) : Number(value)
    setEntryMode(inputMode === 'fiat' ? 'fiat' : 'unit')
    setAmountTextValue(value)
    setValueSats(sats)
    setSendInfo({ ...sendInfo, satoshis: sats })
  }

  const handleSelectAsset = (asset: AssetOption | null) => {
    setShowAssetSelector(false)
    setSelectedAsset(asset)
    if (asset) {
      if (isBTCAddress(recipient)) {
        return setError('Assets can only be sent to Arkade addresses')
      }
      setSendInfo({
        ...sendInfo,
        account: undefined,
        address: '',
        assets: [{ assetId: asset.assetId, amount: BigInt(0) }],
        satoshis: 0,
      })
    } else {
      setSendInfo({ ...sendInfo, account: undefined, assets: undefined, satoshis: 0 })
    }
    setAmountTextValue('')
  }

  const handleRecipientChange = (recipient: string) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    setRecipient(recipient)
    setReadyToParse(false)
    setRawScanData('')
    timeoutRef.current = setTimeout(() => setReadyToParse(true), RECIPIENT_DEBOUNCE_MS)
  }

  const handleContinue = async () => {
    setProcessing(true)
    const satoshis = sendInfo.satoshis ?? 0
    try {
      if (sendInfo.lnUrl && lnUrlResponse) {
        // Check if Ark method is available
        const arkMethod = lnUrlResponse.transferAmounts?.find((method) => method.method === 'Ark' && method.available)

        if (arkMethod) {
          // Fetch Ark address instead of Lightning invoice
          const arkResponse = await fetchArkAddress(sendInfo.lnUrl)
          if (!isValidArkAddress(arkResponse.address)) {
            handleError('Invalid Arkade address received from LNURL')
            return
          }
          setSendInfo((prev) => ({
            ...prev,
            arkAddress: arkResponse.address,
            invoice: undefined,
            pendingLnSend: undefined,
          }))
        } else {
          // No Ark method: fetch a BOLT11 and pay it through the RFQ Lightning
          // path (exact-out, zero spread — no fee to deduct from the amount)
          if (satoshis < 1) return handleError('Amount too low')
          const invoice = await fetchInvoice(sendInfo.lnUrl, Number(satoshis), '')
          setSendInfo((prev) => ({
            ...prev,
            arkAddress: undefined,
            invoice,
            pendingLnSend: invoice === prev.invoice ? prev.pendingLnSend : undefined,
          }))
        }
      } else {
        setSendInfo({ ...sendInfo, satoshis })
      }
      setProceed(true)
    } catch (error) {
      handleError(error)
    }
  }

  const handleEnter = () => {
    if (!buttonDisabled) return handleContinue()
    if (!amount && focus === 'recipient') setFocus('amount')
    if (!recipient && focus === 'amount') setFocus('recipient')
  }

  const handleFocus = () => {
    if (isMobileBrowser) setKeys(true)
  }

  const applySendAll = () => {
    if (sendInfo.account) {
      setSendInfo({
        ...sendInfo,
        account: { ...sendInfo.account, amount: sendInfo.account.balance },
        assets: [{ assetId: sendInfo.account.source.assetId, amount: sendInfo.account.source.balance }],
        satoshis: 0,
      })
      setAmountTextValue(centsToUnits(sendInfo.account.balance, sendInfo.account.decimals))
    } else if (isAssetSend && selectedAsset) {
      const { assetId, balance, decimals } = selectedAsset
      const assets = [{ assetId, amount: balance }]
      setSendInfo({ ...sendInfo, assets, satoshis: 0 })
      setAmountTextValue(centsToUnits(balance, decimals))
    } else {
      setAmount(liquidBalance)
      setValueSats(liquidBalance)
      setSendInfo({ ...sendInfo, satoshis: liquidBalance })
      setAmountTextValue(getTextValue(liquidBalance))
    }
  }

  const handleSendAll = () => {
    if (reserveApplied) setShowReserveModal(true)
    else applySendAll()
  }

  const confirmSendAll = () => {
    setShowReserveModal(false)
    applySendAll()
  }

  const Available = () => {
    if (isAssetSend && activeAsset) {
      return (
        <div onClick={handleSendAll} style={{ cursor: 'pointer' }}>
          <Text color='neutral-500' smaller>
            {`${prettyAssetAmount(activeAsset.balance, activeAsset.decimals)} ${activeAsset.ticker} available`}
          </Text>
        </div>
      )
    }

    const amount = fiatEntry
      ? prettyFiatAmount(liquidBalance ? toFiat(liquidBalance) : 0, config.currency)
      : prettyUnitBalance(liquidBalance)

    return (
      <div onClick={handleSendAll} style={{ cursor: 'pointer' }}>
        <Text color='neutral-500' smaller>
          {`${amount} available`}
        </Text>
      </div>
    )
  }

  const { address, arkAddress, lnUrl, invoice, satoshis } = sendInfo

  const assetAmt = sendInfo.account?.amount ?? sendInfo.assets?.[0]?.amount ?? BigInt(0)

  const buttonDisabled = isAssetSend
    ? !(arkAddress && assetAmt > 0) ||
      (activeAsset ? assetAmt > activeAsset.balance : true) ||
      Boolean(recipientError) ||
      aspInfo.unreachable ||
      Boolean(error) ||
      processing
    : !((address || arkAddress || lnUrl || invoice) && satoshis && satoshis > 0) ||
      (lnUrlResponse?.maxSendable && satoshis > lnUrlResponse.maxSendable) ||
      (lnUrlResponse?.minSendable && satoshis < lnUrlResponse.minSendable) ||
      amountIsAboveMaxLimit(satoshis) ||
      amountIsBelowMinLimit(satoshis) ||
      satoshis > liquidBalance ||
      aspInfo.unreachable ||
      Boolean(error) ||
      satoshis < 1 ||
      processing

  // unverified assets are never offered in the picker; they can still arrive
  // preselected via sendInfo.assets from the Assets app detail screen
  const verifiedAssetOptions = assetOptions.filter((asset) => isVerifiedAsset(asset.assetId))

  // currency designation only (ticker fallback) — the ticker already rides
  // with the amounts on the right, and long asset names collide with the
  // balance column on narrow screens
  const assetLabelFor = (asset: AssetOption) =>
    verifiedDesignatedCurrency(aspInfo.network, asset.assetId, isVerifiedAsset) ?? asset.ticker
  const selectedAssetLabel = activeAsset ? assetLabelFor(activeAsset) : 'Bitcoin'
  const selectedAssetBalance = activeAsset
    ? `${prettyAssetAmount(activeAsset.balance, activeAsset.decimals)} ${activeAsset.ticker} available`
    : `${prettyUnitBalance(liquidBalance)} available`

  const overlayOpen = scan || (keys && !amountIsReadOnly)
  const sendOverlayStyle = { ...overlayStyle, position: 'fixed' as const, zIndex: 20 }

  const Keys = () => (
    <Keyboard
      asset={activeAsset ?? undefined}
      back={() => setKeys(false)}
      defaultMode={fiatEntry ? 'fiat' : config.unit === Unit.BTC ? 'btc' : 'sats'}
      onSave={handleKeyboardAmountSave}
    />
  )

  if (keys && !amountIsReadOnly) {
    return prefersReducedMotion ? (
      <div style={sendOverlayStyle}>
        <Keys />
      </div>
    ) : (
      <AnimatePresence>
        <motion.div
          key='keyboard'
          variants={overlaySlideUp}
          initial='initial'
          animate='animate'
          exit='exit'
          style={sendOverlayStyle}
        >
          <Keys />
        </motion.div>
      </AnimatePresence>
    )
  }

  if (scan) {
    // an element, never a component defined here: a fresh component type on
    // every render remounts the scanner, and each remount asks for the camera
    const scanner = (
      <Scanner
        close={() => setScan(false)}
        label='Recipient address'
        onData={(data) => {
          setRecipient(data)
          setRawScanData(data)
          setReadyToParse(true)
        }}
        onError={smartSetError}
      />
    )
    return prefersReducedMotion ? (
      <div style={sendOverlayStyle}>{scanner}</div>
    ) : (
      <AnimatePresence>
        <motion.div
          key='scanner'
          variants={overlaySlideUp}
          initial='initial'
          animate='animate'
          exit='exit'
          style={sendOverlayStyle}
        >
          {scanner}
        </motion.div>{' '}
      </AnimatePresence>
    )
  }

  return (
    <>
      <div
        /* @ts-expect-error inert is valid HTML but React types lag behind */
        inert={overlayOpen || undefined}
        className='send-form'
        style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
      >
        <Header text='Send' back />
        <Content>
          <Padded>
            <FlexCol gap='1.25rem' className='send-form-stack'>
              <ErrorMessage error={Boolean(error)} text={error} />
              <InputAddress
                error={recipientError}
                focus={focus === 'recipient'}
                label='Recipient address'
                name='send-address'
                onChange={handleRecipientChange}
                onEnter={handleEnter}
                openScan={() => {
                  setKeys(false)
                  setScan(true)
                }}
                value={recipient}
              />
              {brantaLoading ? (
                <Text color='neutral-500' smaller>
                  Verifying address...
                </Text>
              ) : null}
              {brantaPayment
                ? (() => {
                    const card = (
                      <Shadow>
                        <FlexRow between padding='0.75rem'>
                          <FlexCol gap='0.1rem'>
                            <Text smaller>{brantaPayment.platform}</Text>
                            {brantaPayment.description ? (
                              <Text smaller color='neutral-500'>
                                {brantaPayment.description}
                              </Text>
                            ) : null}
                            <Text smaller color='neutral-500'>
                              Verified by Branta
                            </Text>
                          </FlexCol>
                          {(() => {
                            const logoUrl =
                              effectiveTheme === Themes.Light
                                ? (brantaPayment.platformLogoLightUrl ?? brantaPayment.platformLogoUrl)
                                : brantaPayment.platformLogoUrl
                            return logoUrl ? (
                              <img src={logoUrl} alt={brantaPayment.platform} width={48} height={48} />
                            ) : null
                          })()}
                        </FlexRow>
                      </Shadow>
                    )
                    // Only wrap in an anchor when there's a real verify URL; an <a> without href is a
                    // placeholder link that screen readers may still announce.
                    return brantaVerifyUrl ? (
                      <a
                        href={brantaVerifyUrl}
                        target='_blank'
                        rel='noreferrer'
                        style={{ textDecoration: 'none', display: 'block', cursor: 'pointer' }}
                      >
                        {card}
                      </a>
                    ) : (
                      card
                    )
                  })()
                : null}
              {verifiedAssetOptions.length > 0 || selectedAsset ? (
                <FlexCol gap='0.5rem' className='send-asset-field'>
                  <Text smaller color='neutral-500'>
                    Asset
                  </Text>
                  <DropdownMenu
                    open={showAssetSelector}
                    onOpenChange={(open: any) => {
                      if (open) hapticLight()
                      setShowAssetSelector(open)
                    }}
                    modal={false}
                  >
                    <DropdownMenuTrigger
                      aria-expanded={showAssetSelector}
                      className='send-asset-trigger'
                      data-testid='asset-selector'
                    >
                      <span className='send-asset-trigger__main'>
                        <AssetIcon asset={activeAsset} />
                        <span className='send-asset-trigger__copy'>
                          <span className='send-asset-trigger__name'>
                            {selectedAssetLabel}
                            {activeAsset && !isVerifiedAsset(activeAsset.assetId) ? <UnverifiedBadge /> : null}
                          </span>
                          <span className='send-asset-trigger__balance'>{selectedAssetBalance}</span>
                        </span>
                      </span>
                      <span className='send-asset-trigger__chevron' aria-hidden='true'>
                        {showAssetSelector ? '▲' : '▼'}
                      </span>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className='send-asset-menu' align='start' side='bottom' sideOffset={8}>
                      <FlexCol gap='0.25rem'>
                        {activeAsset ? (
                          <DropdownMenuItem className='send-asset-option' onClick={() => handleSelectAsset(null)}>
                            <span className='send-asset-option__main'>
                              <AssetIcon asset={null} />
                              <span>
                                <span className='send-asset-option__name'>Bitcoin</span>
                              </span>
                            </span>
                            <span className='send-asset-option__amount'>{prettyUnitBalance(liquidBalance)}</span>
                          </DropdownMenuItem>
                        ) : null}
                        {verifiedAssetOptions
                          .filter(
                            (asset) =>
                              asset.assetId !== activeAsset?.assetId &&
                              asset.assetId !== sendInfo.account?.source.assetId,
                          )
                          .map((asset) => (
                            <DropdownMenuItem
                              key={asset.assetId}
                              className='send-asset-option'
                              onClick={() => handleSelectAsset(asset)}
                              data-testid={`asset-${asset.ticker.toLowerCase()}-option`}
                            >
                              <span className='send-asset-option__main'>
                                <AssetIcon asset={asset} />
                                <span>
                                  <span className='send-asset-option__name'>{assetLabelFor(asset)}</span>
                                </span>
                              </span>
                              <span className='send-asset-option__amount'>
                                {prettyAssetAmount(asset.balance, asset.decimals)} {asset.ticker}
                              </span>
                            </DropdownMenuItem>
                          ))}
                      </FlexCol>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </FlexCol>
              ) : null}
              <FlexCol gap='0.5rem'>
                <InputAmount
                  label='Amount'
                  name='send-amount'
                  valueSats={valueSats}
                  right={<Available />}
                  onEnter={handleEnter}
                  onFocus={handleFocus}
                  onMax={handleSendAll}
                  value={amountTextValue}
                  readOnly={amountIsReadOnly}
                  onChange={handleAmountChange}
                  onModeChange={handleEntryModeChange}
                  mode={entryMode}
                  switchable
                  min={lnUrlResponse?.minSendable}
                  max={lnUrlResponse?.maxSendable}
                  asset={activeAsset ?? undefined}
                  focus={focus === 'amount' && !isMobileBrowser}
                />
              </FlexCol>
              {deductFromAmount ? <InfoLine color='orange' text='Fees will be deducted from the amount sent' /> : null}
            </FlexCol>
          </Padded>
        </Content>
        <ButtonsOnBottom>
          <Button onClick={handleContinue} label={label} disabled={buttonDisabled} />
        </ButtonsOnBottom>
      </div>
      <SheetModal isOpen={showReserveModal} onClose={() => setShowReserveModal(false)}>
        <FlexCol gap='1rem'>
          <Text bold>Balance reserve</Text>
          <Text color='neutral-500' small wrap>
            {`${DUST_AMOUNT} sats are kept in reserve to protect your assets. Your max sendable amount is ${prettyNumber(liquidBalance)} sats.`}
          </Text>
          <FlexCol gap='0.5rem'>
            <Button onClick={confirmSendAll} label='Send max' />
            <Button onClick={() => setShowReserveModal(false)} label='Cancel' secondary />
          </FlexCol>
        </FlexCol>
      </SheetModal>
    </>
  )
}
